import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, ReplaySubject } from "rxjs";
import { buildParams } from "app/shared/helpers/helpers";
import { ApiAdminService } from "app/core/services/api-admin.service";
import { catchError, filter, tap } from "rxjs/operators";
import { Trunkset } from "app/features/nconnect-voice/interfaces/trunkset/trunkset.interface";
import { NconnectAccountSelectionService } from "app/features/nconnect-voice/services/nconnect-account-selection/nconnect-account-selection.service";
import { TrunksetFormData } from "app/features/nconnect-voice/interfaces/trunkset/form/trunkset.form-data.interface";
import { TrunksetRequestBody } from "app/features/nconnect-voice/interfaces/trunkset/api/trunkset.request-body.interface";
import { NconnectApiResponse } from "app/features/nconnect-voice/interfaces/nconnect-api-response.interface";
import { HttpErrorResponse } from "@angular/common/http";
import { NGXLogger } from "ngx-logger";

// API Documentation: https://confluence.nfon.net/display/VOICE/%5BWIP%5D+Endpoints+Backend
@Injectable({
  providedIn: "root",
})
export class TrunksetDataService {
  private firstTrunksetIdSource: ReplaySubject<number> =
    new ReplaySubject<number>(1);
  private trunksetsSource: BehaviorSubject<Trunkset[] | null> =
    new BehaviorSubject<Trunkset[] | null>(null);

  private trunksetsWithFraudIssuesSource: BehaviorSubject<
    Trunkset[] | undefined
  > = new BehaviorSubject<Trunkset[] | undefined>(undefined);
  private trunksetsWithSpendingManagementIssuesSource: BehaviorSubject<
    Trunkset[] | undefined
  > = new BehaviorSubject<Trunkset[] | undefined>(undefined);

  private endpointUrl: string;
  public firstTrunksetId$: Observable<number> =
    this.firstTrunksetIdSource.asObservable();
  public trunksets$: Observable<Trunkset[] | null> =
    this.trunksetsSource.asObservable();

  public trunksetsWithFraudIssues$: Observable<Trunkset[] | undefined> =
    this.trunksetsWithFraudIssuesSource.asObservable();
  public trunksetsWithSpendingManagementIssues$: Observable<
    Trunkset[] | undefined
  > = this.trunksetsWithSpendingManagementIssuesSource.asObservable();

  constructor(
    private apiAdminService: ApiAdminService,
    private nconnectAccountSelectionService: NconnectAccountSelectionService,
    private logger: NGXLogger,
  ) {
    this.setEndpointUrl();
  }

  /**
   * Update the trunkset item and send the updated item via .next() to data table service
   */
  public updateTrunkset(
    trunksetId: number,
    formData: TrunksetFormData,
  ): Observable<NconnectApiResponse<Trunkset>> {
    return this.apiAdminService
      .put<
        NconnectApiResponse<Trunkset>
      >(`${this.endpointUrl}/${trunksetId}`, this.getAdjustedRequestBodyData(formData))
      .pipe(
        tap((trunksetApiResponse: NconnectApiResponse<Trunkset>) => {
          this.findEditedTrunksetAndEmitTrunksets(trunksetApiResponse.data[0]);
        }),
      );
  }

  /**
   * GET ALL trunksets
   */
  public getTrunksets(
    search: string = "",
    page: number = 1,
    perPage: number = 25,
  ): Observable<NconnectApiResponse<Trunkset>> {
    const params: string = buildParams(search, page, perPage);

    return this.apiAdminService
      .get<NconnectApiResponse<Trunkset>>(`${this.endpointUrl}?${params}`)
      .pipe(
        tap((apiResponse: NconnectApiResponse<Trunkset>) => {
          const trunksets: Trunkset[] = apiResponse.data;

          this.emitTrunksets(trunksets);
          this.emitFirstTrunksetId(trunksets);

          this.setTrunksetsWithFraudIssues(trunksets);
          this.setTrunksetsWithSpendingManagementIssues(trunksets);
        }),
        catchError((error: HttpErrorResponse) => {
          this.logger.error("Failed fetching Trunksets:", error);
          return of([] as any);
        }),
      );
  }

  public getValueOfTrunksets(): Trunkset[] {
    return this.trunksetsSource.getValue() || [];
  }

  private findEditedTrunksetAndEmitTrunksets(editedTrunkset: Trunkset): void {
    const trunksets: Trunkset[] = this.getValueOfTrunksets();
    const indexOfEditedTrunkset: number = trunksets.findIndex(
      (trunkset: Trunkset) => trunkset.ID === editedTrunkset?.ID,
    );
    trunksets[indexOfEditedTrunkset] = editedTrunkset;
    this.emitTrunksets(trunksets);
  }

  private emitTrunksets(trunksets: Trunkset[]): void {
    this.trunksetsSource.next(trunksets);
  }

  private emitFirstTrunksetId(trunksets: Trunkset[]): void {
    const firstTrunksetId: number = trunksets[0]?.ID || 0;
    this.firstTrunksetIdSource.next(firstTrunksetId);
  }

  private setTrunksetsWithFraudIssues(
    trunksets: Trunkset[] = {} as Trunkset[],
  ): void {
    const trunksetsWithFraudIssues: Trunkset[] = trunksets.filter(
      (trunkset: Trunkset) => trunkset.OUTBOUND_BLACKLIST_FRAUD_PROFILE_ID > 1,
    );
    this.trunksetsWithFraudIssuesSource.next(trunksetsWithFraudIssues);
  }

  private setTrunksetsWithSpendingManagementIssues(
    trunksets: Trunkset[],
  ): void {
    const trunksetsWithSpendingManagementIssues: Trunkset[] = trunksets.filter(
      (trunkset: Trunkset) =>
        trunkset.OUTBOUND_BLACKLIST_OVERRIDE_PROFILE_ID > 1,
    );
    this.trunksetsWithSpendingManagementIssuesSource.next(
      trunksetsWithSpendingManagementIssues,
    );
  }

  private getAdjustedRequestBodyData(
    formData: TrunksetFormData,
  ): TrunksetRequestBody {
    return {
      NAME: formData.name,
      STRATEGY: formData.loadBalancingStrategy,
      INBOUND_BLACKLIST_PROFILE_ID: formData.blacklistInboundId,
      OUTBOUND_BLACKLIST_PROFILE_ID: formData.blacklistOutboundId,
    };
  }

  private setEndpointUrl(): void {
    this.nconnectAccountSelectionService.isNconnectAccountSelected$
      .pipe(filter((hasNconnectAccess: boolean | null) => !!hasNconnectAccess))
      .subscribe(() => {
        this.endpointUrl = `${this.nconnectAccountSelectionService.getNconnectApiUrl()}/trunkset`;
      });
  }
}
